import { style, useTool } from '../editor.ts'
import { clientToWorld } from '../canvas/viewportRef.ts'
import { normalizeRect } from '../scene/geometry.ts'
import { createEllipse, createRect } from '../scene/factory.ts'
import {
  abortCommit,
  beginCommit,
  endCommit,
  insertNodes,
  removeNodes,
  scene,
  select,
  updateNode,
} from '../scene/store.ts'
import type { Point, Rect, SceneNode } from '../scene/types.ts'
import { startDrag } from '../util/drag.ts'
import type { Tool } from './types.ts'

export type ShapeKind = 'rect' | 'ellipse'

/** Size used when a shape tool is clicked rather than dragged. */
const CLICK_SIZE = { w: 160, h: 120 }

function seed(kind: ShapeKind, at: Point): SceneNode {
  const r: Rect = { x: at.x, y: at.y, w: 1, h: 1 }
  switch (kind) {
    case 'rect':
      return {
        ...createRect(r),
        fill: style.fill,
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
        cornerRadius: style.cornerRadius,
      }
    case 'ellipse':
      return {
        ...createEllipse(r),
        fill: style.fill,
        stroke: style.stroke,
        strokeWidth: style.strokeWidth,
      }
  }
}

/** Apply shift (square) and alt (from center) to a drag-created box. */
function constrainBox(start: Point, p: Point, shift: boolean, alt: boolean): Rect {
  let r = normalizeRect(start, p)
  if (shift) {
    const s = Math.max(r.w, r.h)
    r = {
      w: s,
      h: s,
      x: p.x < start.x ? start.x - s : start.x,
      y: p.y < start.y ? start.y - s : start.y,
    }
  }
  if (alt) r = { x: start.x - r.w, y: start.y - r.h, w: r.w * 2, h: r.h * 2 }
  return { ...r, w: Math.max(1, r.w), h: Math.max(1, r.h) }
}

export function makeShapeTool(kind: ShapeKind): Tool {
  const label = kind === 'rect' ? 'Rectangle' : 'Ellipse'

  return {
    cursor: 'crosshair',

    onPointerDown(ctx) {
      const start = ctx.world
      const node = seed(kind, start)

      // One commit spans create and size, so the shape is a single undo step.
      beginCommit([node.id], true)
      insertNodes([node])
      select([node.id])

      let dragged = false

      startDrag({
        onMove(e) {
          const p = clientToWorld(e)
          dragged =
            dragged || Math.hypot(p.x - start.x, p.y - start.y) * scene.camera.z >= 3
          if (!dragged) return

          updateNode(node.id, constrainBox(start, p, e.shiftKey, e.altKey))
        },

        onUp() {
          if (!dragged) {
            updateNode(node.id, {
              x: start.x - CLICK_SIZE.w / 2,
              y: start.y - CLICK_SIZE.h / 2,
              ...CLICK_SIZE,
            })
          }
          endCommit(label)
          useTool('select')
        },

        onCancel() {
          removeNodes([node.id])
          abortCommit()
          useTool('select')
        },
      })
    },
  }
}
